<script setup>
import { computed, ref, watch } from 'vue';
import { useStore } from 'vuex';
import { useI18n } from 'vue-i18n';
import { format } from 'date-fns';
import { useAlert } from 'dashboard/composables';
import { useMapGetter } from 'dashboard/composables/store';
import NextButton from 'dashboard/components-next/button/Button.vue';
import Input from 'dashboard/components-next/input/Input.vue';
import WhatsappTemplates from 'dashboard/components/widgets/conversation/WhatsappTemplates/Modal.vue';
import ContentTemplates from 'dashboard/components/widgets/conversation/ContentTemplates/ContentTemplatesModal.vue';
import ScheduledWorkflowsAPI from 'custom/api/scheduledWorkflows';
import { useOperationalCatalog } from 'custom/composables/useOperationalCatalog';
import { notifyScheduledWorkflowTimeline } from 'custom/composables/useScheduledWorkflowTimeline';

const props = defineProps({
  conversationId: { type: Number, required: true },
  inbox: { type: Object, default: () => ({}) },
  draftMessage: { type: String, default: '' },
  enableWhatsAppTemplates: { type: Boolean, default: false },
  enableContentTemplates: { type: Boolean, default: false },
  disabled: { type: Boolean, default: false },
});

const { t } = useI18n();
const store = useStore();
const macros = useMapGetter('macros/getMacros');
const cannedResponses = useMapGetter('getCannedResponses');
const labels = useMapGetter('labels/getLabels');
const teams = useMapGetter('teams/getTeams');
const agents = useMapGetter('agents/getAgents');
const { filterRecords, isLoading: isCatalogLoading } = useOperationalCatalog([
  'macro',
  'canned_response',
  'label',
]);

const enabled = ref(false);
const show = ref(false);
const activeTab = ref('schedule');
const scheduledAt = ref('');
const actions = ref([]);
const actionType = ref('text');
const actionContent = ref('');
const resourceId = ref('');
const workflows = ref([]);
const isLoading = ref(false);
const isSaving = ref(false);
const editingWorkflowId = ref(null);
const showWhatsAppTemplates = ref(false);
const showContentTemplates = ref(false);

const backendErrorTranslations = {
  'At least one action is required': () =>
    t('CONVERSATION.SCHEDULED_WORKFLOWS.ERROR_REASON.ACTION_REQUIRED'),
  'Unsupported scheduled action': () =>
    t('CONVERSATION.SCHEDULED_WORKFLOWS.ERROR_REASON.ACTION_UNSUPPORTED'),
  'Message text cannot be blank': () =>
    t('CONVERSATION.SCHEDULED_WORKFLOWS.ERROR_REASON.MESSAGE_BLANK'),
  'Template data is incomplete': () =>
    t('CONVERSATION.SCHEDULED_WORKFLOWS.ERROR_REASON.TEMPLATE_INCOMPLETE'),
  'Macro is not visible to this user': () =>
    t('CONVERSATION.SCHEDULED_WORKFLOWS.ERROR_REASON.MACRO_NOT_VISIBLE'),
  'Macro contains an unsupported action': () =>
    t('CONVERSATION.SCHEDULED_WORKFLOWS.ERROR_REASON.MACRO_UNSUPPORTED'),
  'Webhook destination is missing': () =>
    t('CONVERSATION.SCHEDULED_WORKFLOWS.ERROR_REASON.WEBHOOK_MISSING'),
  'Only pending workflows can be edited': () =>
    t('CONVERSATION.SCHEDULED_WORKFLOWS.ERROR_REASON.PENDING_EDIT'),
  'Only pending workflows can be cancelled': () =>
    t('CONVERSATION.SCHEDULED_WORKFLOWS.ERROR_REASON.PENDING_CANCEL'),
  'The account no longer exists': () =>
    t('CONVERSATION.SCHEDULED_WORKFLOWS.ERROR_REASON.ACCOUNT_MISSING'),
  'The conversation no longer exists': () =>
    t('CONVERSATION.SCHEDULED_WORKFLOWS.ERROR_REASON.CONVERSATION_MISSING'),
  'The contact is blocked or no longer exists': () =>
    t('CONVERSATION.SCHEDULED_WORKFLOWS.ERROR_REASON.CONTACT_UNAVAILABLE'),
  'The scheduling user no longer exists': () =>
    t('CONVERSATION.SCHEDULED_WORKFLOWS.ERROR_REASON.USER_MISSING'),
  'The scheduling user no longer has access to the conversation': () =>
    t('CONVERSATION.SCHEDULED_WORKFLOWS.ERROR_REASON.ACCESS_DENIED'),
  'Access to the conversation was denied': () =>
    t('CONVERSATION.SCHEDULED_WORKFLOWS.ERROR_REASON.ACCESS_DENIED'),
  'Scheduled attachments are no longer available': () =>
    t('CONVERSATION.SCHEDULED_WORKFLOWS.ERROR_REASON.ATTACHMENT_UNAVAILABLE'),
  'The WhatsApp message window is closed; an approved template is required':
    () => t('CONVERSATION.SCHEDULED_WORKFLOWS.ERROR_REASON.WHATSAPP_WINDOW'),
  'The scheduled template is no longer approved or available': () =>
    t('CONVERSATION.SCHEDULED_WORKFLOWS.ERROR_REASON.TEMPLATE_UNAVAILABLE'),
  'The scheduled webhook destination is unavailable': () =>
    t('CONVERSATION.SCHEDULED_WORKFLOWS.ERROR_REASON.WEBHOOK_UNAVAILABLE'),
};

const translatedBackendError = (message, fallback) =>
  backendErrorTranslations[message]?.() || fallback;

const availableMacros = computed(() => filterRecords('macro', macros.value));
const availableCannedResponses = computed(() =>
  filterRecords('canned_response', cannedResponses.value)
);
const availableLabels = computed(() => filterRecords('label', labels.value));
const currentResources = computed(() => {
  if (actionType.value === 'macro') return availableMacros.value;
  if (actionType.value === 'canned_response') {
    return availableCannedResponses.value;
  }
  if (actionType.value === 'label') return availableLabels.value;
  return [];
});
const selectedResource = computed(() =>
  currentResources.value.find(
    resource => resource.id === Number(resourceId.value)
  )
);
const macroPreviewActions = computed(
  () => selectedResource.value?.actions || []
);
const cannedResponseCode = computed(() =>
  selectedResource.value ? `/${selectedResource.value.short_code}` : ''
);
const isTextAction = computed(() => actionType.value === 'text');
const canAddAction = computed(() =>
  isTextAction.value
    ? actionContent.value.trim().length > 0
    : !!resourceId.value
);
const canSave = computed(() => !!scheduledAt.value && actions.value.length > 0);
const browserTimezone =
  Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';

const localDateTime = date => format(date, "yyyy-MM-dd'T'HH:mm");

const minimumDateTime = computed(() =>
  localDateTime(new Date(Date.now() + 60 * 1000))
);

const resetForm = () => {
  scheduledAt.value = localDateTime(new Date(Date.now() + 2 * 60 * 1000));
  actions.value = props.draftMessage.trim()
    ? [{ type: 'text', content: props.draftMessage.trim() }]
    : [];
  actionType.value = 'text';
  actionContent.value = '';
  resourceId.value = '';
  editingWorkflowId.value = null;
};

const loadWorkflows = async () => {
  if (!props.conversationId) return;
  isLoading.value = true;
  try {
    const { data } = await ScheduledWorkflowsAPI.list(props.conversationId);
    enabled.value = data.enabled;
    workflows.value = data.workflows || [];
  } catch {
    enabled.value = false;
  } finally {
    isLoading.value = false;
  }
};

const open = async () => {
  resetForm();
  activeTab.value = 'schedule';
  show.value = true;
  await Promise.all([
    store.dispatch('macros/get'),
    store.dispatch('getCannedResponse'),
    store.dispatch('labels/get'),
    loadWorkflows(),
  ]);
};

const close = () => {
  show.value = false;
};

const addAction = () => {
  if (!canAddAction.value) return;
  if (isTextAction.value) {
    actions.value.push({ type: 'text', content: actionContent.value.trim() });
    actionContent.value = '';
  } else {
    actions.value.push({
      type: actionType.value,
      id: Number(resourceId.value),
    });
    resourceId.value = '';
  }
};

const addTemplate = payload => {
  actions.value.push({ type: 'template', payload });
  showWhatsAppTemplates.value = false;
  showContentTemplates.value = false;
};

const moveAction = (index, direction) => {
  const target = index + direction;
  if (target < 0 || target >= actions.value.length) return;
  const reordered = [...actions.value];
  [reordered[index], reordered[target]] = [reordered[target], reordered[index]];
  actions.value = reordered;
};

const removeAction = index => {
  actions.value.splice(index, 1);
};

const resourceName = action => {
  const records = {
    macro: availableMacros.value,
    canned_response: availableCannedResponses.value,
    label: availableLabels.value,
  }[action.type];
  const record = records?.find(item => item.id === action.id);
  return record?.name || record?.short_code || record?.title || `#${action.id}`;
};

const actionSummary = action => {
  if (action.type === 'text') return action.content;
  if (action.type === 'template') {
    return (
      action.payload?.templateParams?.name ||
      t('CONVERSATION.SCHEDULED_WORKFLOWS.TEMPLATE')
    );
  }
  return resourceName(action);
};

const actionTypeLabel = action =>
  ({
    text: t('CONVERSATION.SCHEDULED_WORKFLOWS.TEXT'),
    canned_response: t('CONVERSATION.SCHEDULED_WORKFLOWS.CANNED_RESPONSE'),
    macro: t('CONVERSATION.SCHEDULED_WORKFLOWS.MACRO'),
    label: t('CONVERSATION.SCHEDULED_WORKFLOWS.LABEL'),
    template: t('CONVERSATION.SCHEDULED_WORKFLOWS.TEMPLATE'),
  })[action.type] || action.type;

const macroActionTranslations = {
  send_message: () =>
    t('CONVERSATION.SCHEDULED_WORKFLOWS.PREVIEW.ACTIONS.SEND_MESSAGE'),
  add_label: () =>
    t('CONVERSATION.SCHEDULED_WORKFLOWS.PREVIEW.ACTIONS.ADD_LABEL'),
  assign_team: () =>
    t('CONVERSATION.SCHEDULED_WORKFLOWS.PREVIEW.ACTIONS.ASSIGN_TEAM'),
  assign_agent: () =>
    t('CONVERSATION.SCHEDULED_WORKFLOWS.PREVIEW.ACTIONS.ASSIGN_AGENT'),
  mute_conversation: () =>
    t('CONVERSATION.SCHEDULED_WORKFLOWS.PREVIEW.ACTIONS.MUTE_CONVERSATION'),
  change_status: () =>
    t('CONVERSATION.SCHEDULED_WORKFLOWS.PREVIEW.ACTIONS.CHANGE_STATUS'),
  remove_label: () =>
    t('CONVERSATION.SCHEDULED_WORKFLOWS.PREVIEW.ACTIONS.REMOVE_LABEL'),
  remove_assigned_agent: () =>
    t('CONVERSATION.SCHEDULED_WORKFLOWS.PREVIEW.ACTIONS.REMOVE_ASSIGNED_AGENT'),
  remove_assigned_team: () =>
    t('CONVERSATION.SCHEDULED_WORKFLOWS.PREVIEW.ACTIONS.REMOVE_ASSIGNED_TEAM'),
  resolve_conversation: () =>
    t('CONVERSATION.SCHEDULED_WORKFLOWS.PREVIEW.ACTIONS.RESOLVE_CONVERSATION'),
  snooze_conversation: () =>
    t('CONVERSATION.SCHEDULED_WORKFLOWS.PREVIEW.ACTIONS.SNOOZE_CONVERSATION'),
  change_priority: () =>
    t('CONVERSATION.SCHEDULED_WORKFLOWS.PREVIEW.ACTIONS.CHANGE_PRIORITY'),
  send_email_transcript: () =>
    t('CONVERSATION.SCHEDULED_WORKFLOWS.PREVIEW.ACTIONS.SEND_EMAIL_TRANSCRIPT'),
  send_attachment: () =>
    t('CONVERSATION.SCHEDULED_WORKFLOWS.PREVIEW.ACTIONS.SEND_ATTACHMENT'),
  add_private_note: () =>
    t('CONVERSATION.SCHEDULED_WORKFLOWS.PREVIEW.ACTIONS.ADD_PRIVATE_NOTE'),
  send_webhook_event: () =>
    t('CONVERSATION.SCHEDULED_WORKFLOWS.PREVIEW.ACTIONS.SEND_WEBHOOK_EVENT'),
};

const macroActionLabel = actionName =>
  macroActionTranslations[actionName]?.() ||
  t('CONVERSATION.SCHEDULED_WORKFLOWS.PREVIEW.ACTIONS.UNKNOWN');

const recordName = (records, id) =>
  records.find(record => record.id === Number(id))?.name || `#${id}`;

const assignmentValue = (kind, id) => {
  if (['nil', '0'].includes(String(id))) {
    return t('CONVERSATION.SCHEDULED_WORKFLOWS.PREVIEW.UNASSIGNED');
  }
  if (id === 'self') {
    return t('CONVERSATION.SCHEDULED_WORKFLOWS.PREVIEW.CURRENT_AGENT');
  }
  if (id === 'last_responding_agent') {
    return t('CONVERSATION.SCHEDULED_WORKFLOWS.PREVIEW.LAST_RESPONDING_AGENT');
  }
  return kind === 'team'
    ? recordName(teams.value, id)
    : recordName(agents.value, id);
};

const priorityValue = priority =>
  ({
    nil: t('CONVERSATION.SCHEDULED_WORKFLOWS.PREVIEW.PRIORITY.NONE'),
    low: t('CONVERSATION.SCHEDULED_WORKFLOWS.PREVIEW.PRIORITY.LOW'),
    medium: t('CONVERSATION.SCHEDULED_WORKFLOWS.PREVIEW.PRIORITY.MEDIUM'),
    high: t('CONVERSATION.SCHEDULED_WORKFLOWS.PREVIEW.PRIORITY.HIGH'),
    urgent: t('CONVERSATION.SCHEDULED_WORKFLOWS.PREVIEW.PRIORITY.URGENT'),
  })[priority] || priority;

const statusValue = status =>
  ({
    open: t('CONVERSATION.SCHEDULED_WORKFLOWS.PREVIEW.STATUS.OPEN'),
    pending: t('CONVERSATION.SCHEDULED_WORKFLOWS.PREVIEW.STATUS.PENDING'),
    resolved: t('CONVERSATION.SCHEDULED_WORKFLOWS.PREVIEW.STATUS.RESOLVED'),
    snoozed: t('CONVERSATION.SCHEDULED_WORKFLOWS.PREVIEW.STATUS.SNOOZED'),
  })[status] || status;

const attachmentValue = params => {
  const files = selectedResource.value?.files || [];
  const names = params.map(
    id => files.find(file => file.blob_id === Number(id))?.filename
  );
  return names.every(Boolean)
    ? names.join(', ')
    : t('CONVERSATION.SCHEDULED_WORKFLOWS.PREVIEW.ATTACHMENTS', {
        count: params.length,
      });
};

const macroActionValue = action => {
  const params = Array(action.action_params || []);
  const resolvers = {
    assign_team: () => assignmentValue('team', params[0]),
    assign_agent: () => assignmentValue('agent', params[0]),
    change_priority: () => priorityValue(params[0]),
    change_status: () => statusValue(params[0]),
    send_attachment: () => attachmentValue(params),
  };
  return resolvers[action.action_name]?.() || params.join(', ');
};

const save = async () => {
  if (!canSave.value) return;
  isSaving.value = true;
  try {
    const payload = {
      scheduled_at: new Date(scheduledAt.value).toISOString(),
      scheduled_timezone: browserTimezone,
    };
    if (editingWorkflowId.value) {
      await ScheduledWorkflowsAPI.update(editingWorkflowId.value, {
        ...payload,
        actions: actions.value,
      });
    } else {
      await ScheduledWorkflowsAPI.create({
        ...payload,
        conversation_id: props.conversationId,
        idempotency_key:
          window.crypto?.randomUUID?.() ||
          `${Date.now()}-${Math.random().toString(16).slice(2)}`,
        actions: actions.value,
      });
    }
    useAlert(t('CONVERSATION.SCHEDULED_WORKFLOWS.SAVE_SUCCESS'));
    await loadWorkflows();
    notifyScheduledWorkflowTimeline(props.conversationId);
    activeTab.value = 'history';
    resetForm();
  } catch (error) {
    useAlert(
      translatedBackendError(
        error?.response?.data?.error,
        t('CONVERSATION.SCHEDULED_WORKFLOWS.SAVE_ERROR')
      )
    );
  } finally {
    isSaving.value = false;
  }
};

const editSchedule = workflow => {
  editingWorkflowId.value = workflow.id;
  scheduledAt.value = localDateTime(new Date(workflow.scheduled_at));
  actions.value = JSON.parse(JSON.stringify(workflow.input_actions || []));
  activeTab.value = 'schedule';
};

const cancelWorkflow = async workflow => {
  try {
    await ScheduledWorkflowsAPI.cancel(workflow.id);
    useAlert(t('CONVERSATION.SCHEDULED_WORKFLOWS.CANCEL_SUCCESS'));
    await loadWorkflows();
    notifyScheduledWorkflowTimeline(props.conversationId);
  } catch (error) {
    useAlert(
      translatedBackendError(
        error?.response?.data?.error,
        t('CONVERSATION.SCHEDULED_WORKFLOWS.CANCEL_ERROR')
      )
    );
  }
};

const formatScheduledAt = value => format(new Date(value), 'yyyy-MM-dd HH:mm');

const statusLabel = status =>
  ({
    pending: t('CONVERSATION.SCHEDULED_WORKFLOWS.STATUS.pending'),
    processing: t('CONVERSATION.SCHEDULED_WORKFLOWS.STATUS.processing'),
    completed: t('CONVERSATION.SCHEDULED_WORKFLOWS.STATUS.completed'),
    cancelled: t('CONVERSATION.SCHEDULED_WORKFLOWS.STATUS.cancelled'),
    failed: t('CONVERSATION.SCHEDULED_WORKFLOWS.STATUS.failed'),
    partially_failed: t(
      'CONVERSATION.SCHEDULED_WORKFLOWS.STATUS.partially_failed'
    ),
  })[status];

const workflowErrorLabel = error =>
  translatedBackendError(
    error,
    t('CONVERSATION.SCHEDULED_WORKFLOWS.ERROR_REASON.UNKNOWN')
  );

watch(
  () => props.conversationId,
  () => loadWorkflows(),
  { immediate: true }
);
</script>

<template>
  <div>
    <template v-if="enabled">
      <NextButton
        type="button"
        sm
        faded
        slate
        icon="i-ph-clock"
        :disabled="disabled"
        :label="t('CONVERSATION.SCHEDULED_WORKFLOWS.BUTTON')"
        @click="open"
      />

      <woot-modal
        :show="show"
        :on-close="close"
        size="!w-fit !max-w-[calc(100vw-2rem)]"
      >
        <div class="flex max-h-[88vh] w-fit max-w-full flex-col">
          <woot-modal-header
            class="!px-4 sm:!px-6"
            :header-title="t('CONVERSATION.SCHEDULED_WORKFLOWS.TITLE')"
            :header-content="
              t('CONVERSATION.SCHEDULED_WORKFLOWS.TIMEZONE', {
                timezone: browserTimezone,
              })
            "
          />

          <div class="flex gap-2 px-4 pt-4 sm:px-6">
            <NextButton
              type="button"
              :variant="activeTab === 'schedule' ? 'solid' : 'faded'"
              :label="t('CONVERSATION.SCHEDULED_WORKFLOWS.SCHEDULE_TAB')"
              @click="activeTab = 'schedule'"
            />
            <NextButton
              type="button"
              :variant="activeTab === 'history' ? 'solid' : 'faded'"
              :label="t('CONVERSATION.SCHEDULED_WORKFLOWS.HISTORY_TAB')"
              @click="activeTab = 'history'"
            />
          </div>

          <form
            v-if="activeTab === 'schedule'"
            class="flex min-h-0 w-full max-w-full flex-1 flex-col overflow-hidden"
            @submit.prevent="save"
          >
            <div
              class="w-full max-w-full flex-1 overflow-y-auto px-4 py-4 sm:px-6 lg:py-5"
            >
              <section
                data-testid="schedule-time-panel"
                class="mb-4 w-full max-w-full rounded-xl border border-n-weak bg-n-alpha-2 p-4"
              >
                <Input
                  v-model="scheduledAt"
                  type="datetime-local"
                  class="w-full min-w-0 max-w-full"
                  :min="minimumDateTime"
                  :label="t('CONVERSATION.SCHEDULED_WORKFLOWS.DATE')"
                />
              </section>

              <div
                data-testid="schedule-builder-grid"
                class="grid w-full max-w-full grid-cols-1 items-start gap-4 lg:grid-cols-2"
              >
                <section
                  data-testid="action-builder-panel"
                  class="flex w-full min-w-0 max-w-full flex-col gap-4 overflow-hidden rounded-xl border border-n-weak p-4 lg:p-5"
                >
                  <div>
                    <p class="text-sm font-semibold text-n-slate-12">
                      {{ t('CONVERSATION.SCHEDULED_WORKFLOWS.ACTION_BUILDER') }}
                    </p>
                  </div>

                  <label class="text-sm font-medium text-n-slate-12">
                    {{ t('CONVERSATION.SCHEDULED_WORKFLOWS.ACTION_TYPE') }}
                    <select
                      v-model="actionType"
                      class="mt-1 w-full rounded-lg border border-n-weak bg-n-alpha-white px-3 py-2 text-sm text-n-slate-12"
                      @change="resourceId = ''"
                    >
                      <option value="text">
                        {{ t('CONVERSATION.SCHEDULED_WORKFLOWS.TEXT') }}
                      </option>
                      <option value="canned_response">
                        {{
                          t('CONVERSATION.SCHEDULED_WORKFLOWS.CANNED_RESPONSE')
                        }}
                      </option>
                      <option value="macro">
                        {{ t('CONVERSATION.SCHEDULED_WORKFLOWS.MACRO') }}
                      </option>
                      <option value="label">
                        {{ t('CONVERSATION.SCHEDULED_WORKFLOWS.LABEL') }}
                      </option>
                    </select>
                  </label>

                  <textarea
                    v-if="isTextAction"
                    v-model="actionContent"
                    rows="4"
                    class="w-full resize-y rounded-lg border border-n-weak bg-n-alpha-white px-3 py-2 text-sm text-n-slate-12"
                    :placeholder="
                      t('CONVERSATION.SCHEDULED_WORKFLOWS.TEXT_PLACEHOLDER')
                    "
                  />
                  <select
                    v-else
                    v-model="resourceId"
                    class="w-full rounded-lg border border-n-weak bg-n-alpha-white px-3 py-2 text-sm text-n-slate-12"
                    :disabled="isCatalogLoading"
                  >
                    <option value="">
                      {{
                        t('CONVERSATION.SCHEDULED_WORKFLOWS.SELECT_RESOURCE')
                      }}
                    </option>
                    <option
                      v-for="resource in currentResources"
                      :key="resource.id"
                      :value="resource.id"
                    >
                      {{
                        resource.name || resource.short_code || resource.title
                      }}
                    </option>
                  </select>

                  <div
                    v-if="selectedResource"
                    data-testid="resource-preview"
                    class="flex min-w-0 max-w-full flex-col gap-3 overflow-hidden rounded-lg border border-n-weak bg-n-alpha-2 p-4"
                  >
                    <p class="text-xs font-medium uppercase text-n-slate-10">
                      {{ t('CONVERSATION.SCHEDULED_WORKFLOWS.PREVIEW.TITLE') }}
                    </p>

                    <template v-if="actionType === 'canned_response'">
                      <p class="text-sm font-medium text-n-slate-12">
                        {{ cannedResponseCode }}
                      </p>
                      <p class="whitespace-pre-wrap text-sm text-n-slate-11">
                        {{ selectedResource.content }}
                      </p>
                    </template>

                    <template v-else-if="actionType === 'label'">
                      <p class="text-sm font-medium text-n-slate-12">
                        {{ selectedResource.title }}
                      </p>
                      <p
                        v-if="selectedResource.description"
                        class="text-sm text-n-slate-11"
                      >
                        {{ selectedResource.description }}
                      </p>
                    </template>

                    <template v-else-if="actionType === 'macro'">
                      <p class="text-sm font-medium text-n-slate-12">
                        {{ selectedResource.name }}
                      </p>
                      <ol class="flex flex-col gap-2">
                        <li
                          v-for="(macroAction, index) in macroPreviewActions"
                          :key="`${macroAction.action_name}-${index}`"
                          class="flex gap-3 rounded-md bg-n-alpha-1 px-3 py-2"
                        >
                          <span class="text-xs font-medium text-n-slate-10">
                            {{ index + 1 }}
                          </span>
                          <div class="min-w-0 flex-1">
                            <p class="text-sm font-medium text-n-slate-12">
                              {{ macroActionLabel(macroAction.action_name) }}
                            </p>
                            <p
                              v-if="macroActionValue(macroAction)"
                              class="whitespace-pre-wrap break-words text-sm text-n-slate-11"
                            >
                              {{ macroActionValue(macroAction) }}
                            </p>
                          </div>
                        </li>
                      </ol>
                    </template>
                  </div>

                  <div
                    class="flex flex-col gap-3 border-t border-n-weak pt-4 sm:flex-row sm:items-center sm:justify-between"
                  >
                    <div class="flex flex-wrap gap-2">
                      <NextButton
                        v-if="enableWhatsAppTemplates"
                        type="button"
                        faded
                        slate
                        :label="
                          t(
                            'CONVERSATION.SCHEDULED_WORKFLOWS.ADD_WHATSAPP_TEMPLATE'
                          )
                        "
                        @click="showWhatsAppTemplates = true"
                      />
                      <NextButton
                        v-if="enableContentTemplates"
                        type="button"
                        faded
                        slate
                        :label="
                          t(
                            'CONVERSATION.SCHEDULED_WORKFLOWS.ADD_CONTENT_TEMPLATE'
                          )
                        "
                        @click="showContentTemplates = true"
                      />
                    </div>
                    <NextButton
                      type="button"
                      class="shrink-0 sm:ml-auto"
                      :disabled="!canAddAction"
                      :label="t('CONVERSATION.SCHEDULED_WORKFLOWS.ADD_ACTION')"
                      @click="addAction"
                    />
                  </div>
                </section>

                <section
                  data-testid="action-sequence-panel"
                  class="flex min-h-52 w-full min-w-0 max-w-full flex-col gap-4 overflow-hidden rounded-xl border border-n-weak bg-n-alpha-2 p-4 lg:p-5"
                >
                  <div class="flex items-center justify-between gap-3">
                    <div>
                      <p class="text-sm font-semibold text-n-slate-12">
                        {{
                          t('CONVERSATION.SCHEDULED_WORKFLOWS.ACTION_SEQUENCE')
                        }}
                      </p>
                      <p class="mt-1 text-xs text-n-slate-10">
                        {{ actions.length }}
                        {{
                          actions.length === 1
                            ? t('CONVERSATION.SCHEDULED_WORKFLOWS.ACTION')
                            : t('CONVERSATION.SCHEDULED_WORKFLOWS.ACTIONS')
                        }}
                      </p>
                    </div>
                    <span
                      class="flex size-9 items-center justify-center rounded-full bg-n-alpha-1 text-base text-n-slate-11 i-ph-list-numbers"
                    />
                  </div>

                  <div
                    v-if="!actions.length"
                    data-testid="action-sequence-empty"
                    class="flex min-h-32 flex-1 items-center justify-center rounded-lg border border-dashed border-n-weak bg-n-alpha-1 px-4 text-center"
                  >
                    <p class="max-w-xs text-sm text-n-slate-10">
                      {{
                        t(
                          'CONVERSATION.SCHEDULED_WORKFLOWS.ACTION_SEQUENCE_EMPTY'
                        )
                      }}
                    </p>
                  </div>

                  <ol v-else class="flex flex-col gap-2">
                    <li
                      v-for="(action, index) in actions"
                      :key="`${action.type}-${index}`"
                      class="flex min-w-0 max-w-full items-center gap-3 overflow-hidden rounded-lg border border-n-weak bg-n-alpha-white p-3"
                    >
                      <span
                        class="flex size-7 shrink-0 items-center justify-center rounded-full bg-n-alpha-2 text-xs font-semibold text-n-slate-11"
                      >
                        {{ index + 1 }}
                      </span>
                      <div class="min-w-0 flex-1">
                        <p
                          class="text-xs font-medium uppercase text-n-slate-10"
                        >
                          {{ actionTypeLabel(action) }}
                        </p>
                        <p
                          class="mt-0.5 line-clamp-2 whitespace-pre-wrap break-words text-sm text-n-slate-12"
                        >
                          {{ actionSummary(action) }}
                        </p>
                      </div>
                      <div class="flex shrink-0 gap-1">
                        <NextButton
                          type="button"
                          sm
                          faded
                          slate
                          icon="i-ph-arrow-up"
                          :disabled="index === 0"
                          @click="moveAction(index, -1)"
                        />
                        <NextButton
                          type="button"
                          sm
                          faded
                          slate
                          icon="i-ph-arrow-down"
                          :disabled="index === actions.length - 1"
                          @click="moveAction(index, 1)"
                        />
                        <NextButton
                          type="button"
                          sm
                          faded
                          ruby
                          icon="i-ph-trash"
                          @click="removeAction(index)"
                        />
                      </div>
                    </li>
                  </ol>
                </section>
              </div>
            </div>

            <div
              data-testid="schedule-footer"
              class="flex shrink-0 items-center justify-between gap-4 border-t border-n-weak bg-n-alpha-white px-4 py-3 sm:px-6"
            >
              <NextButton
                type="button"
                faded
                slate
                :label="t('CONVERSATION.SCHEDULED_WORKFLOWS.CLOSE')"
                @click="close"
              />
              <NextButton
                type="submit"
                :is-loading="isSaving"
                :disabled="!canSave || isSaving"
                :label="
                  editingWorkflowId
                    ? t('CONVERSATION.SCHEDULED_WORKFLOWS.UPDATE')
                    : t('CONVERSATION.SCHEDULED_WORKFLOWS.SAVE')
                "
              />
            </div>
          </form>

          <div
            v-else
            class="flex w-full flex-col gap-3 overflow-y-auto px-4 py-4 sm:px-6"
          >
            <p v-if="isLoading" class="text-sm text-n-slate-11">
              {{ t('CONVERSATION.SCHEDULED_WORKFLOWS.LOADING') }}
            </p>
            <p v-else-if="!workflows.length" class="text-sm text-n-slate-11">
              {{ t('CONVERSATION.SCHEDULED_WORKFLOWS.EMPTY') }}
            </p>
            <template v-else>
              <article
                v-for="workflow in workflows"
                :key="workflow.id"
                class="flex gap-3 items-center rounded-lg border border-n-weak px-4 py-3"
              >
                <div class="min-w-0 flex-1">
                  <p class="text-sm font-medium text-n-slate-12">
                    {{ formatScheduledAt(workflow.scheduled_at) }}
                  </p>
                  <p class="text-xs text-n-slate-10">
                    {{ statusLabel(workflow.status) }}
                  </p>
                  <p
                    v-if="workflow.last_error"
                    class="mt-1 text-xs text-n-ruby-11"
                  >
                    {{ workflowErrorLabel(workflow.last_error) }}
                  </p>
                </div>
                <template v-if="workflow.status === 'pending'">
                  <NextButton
                    type="button"
                    sm
                    faded
                    slate
                    :label="t('CONVERSATION.SCHEDULED_WORKFLOWS.EDIT')"
                    @click="editSchedule(workflow)"
                  />
                  <NextButton
                    type="button"
                    sm
                    faded
                    ruby
                    :label="t('CONVERSATION.SCHEDULED_WORKFLOWS.CANCEL')"
                    @click="cancelWorkflow(workflow)"
                  />
                </template>
              </article>
            </template>
          </div>
        </div>
      </woot-modal>

      <WhatsappTemplates
        v-model:show="showWhatsAppTemplates"
        :inbox-id="inbox.id"
        @on-send="addTemplate"
        @cancel="showWhatsAppTemplates = false"
      />
      <ContentTemplates
        v-model:show="showContentTemplates"
        :inbox-id="inbox.id"
        @on-send="addTemplate"
        @cancel="showContentTemplates = false"
      />
    </template>
  </div>
</template>
